import * as React from "react";
import {useState, useRef} from "react";
import {IOddUploadResult, OddIqApi} from "../../api/OddIqApi";
import {throwErrorOnNullOrUndefined} from "../../utils/errorUtil";
import {TimerComponent} from "../common/Timer.component";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {useInterval} from "../common/useInterval";

const TERMINAL_STATUSES = ["Completed", "Completed with Errors", "Failed"];
export const POLLING_INTERVAL_MS = 3000;
export const MAX_POLLING_DURATION_MS = 20 * 60 * 1000; // 20 minutes

const ERROR_POLLING_TIMEOUT = "Upload status check timed out after 20 minutes. The upload may still be processing. Please check the database to verify upload completion.";
const ERROR_STATUS_CHECK_FAILED = "Failed to check upload status. Please check the database to verify upload completion.";
const ERROR_START_UPLOAD_FAILED = "Failed to start upload job";

export const UploadOdd: React.FunctionComponent = () => {
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [uploadResult, setUploadResult] = useState<IOddUploadResult | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [jobId, setJobId] = useState<string | null>(null);
    const [progress, setProgress] = useState<string | null>(null);
    const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const stopPolling = (result: IOddUploadResult) => {
        setJobId(null);
        setIsLoading(false);
        setProgress(null);
        setPollingStartTime(null);
        setUploadResult(result);
    };

    useInterval(async () => {
        if (!jobId) return;

        if (pollingStartTime && (Date.now() - pollingStartTime > MAX_POLLING_DURATION_MS)) {
            stopPolling({isSuccess: false, errors: [ERROR_POLLING_TIMEOUT]});
            return;
        }

        OddIqApi.getUploadStatus(jobId)
            .then(({data}) => {
                if (data) {
                    setProgress(data.progress);

                    if (TERMINAL_STATUSES.includes(data.status)) {
                        stopPolling({isSuccess: data.isSuccess ?? false, errors: data.errors || []});
                        clearFileInput();
                    }
                }
            })
            .catch(() => {
                stopPolling({isSuccess: false, errors: [ERROR_STATUS_CHECK_FAILED]});
            });
    }, jobId ? POLLING_INTERVAL_MS : undefined);

    const renderResult = () => {
        if (!uploadResult) return null;

        const isSuccess = uploadResult.isSuccess;
        return (
            <div
                className={`upload-odd__upload-result ${isSuccess ? "upload-odd__upload-result--success" : "upload-odd__upload-result--error"}`}
                data-testid="odd__result">
                <div>
                    <strong>{isSuccess ? "Upload succeeded" : "Upload failed"}</strong>
                </div>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div style={{marginTop: "8px"}}>
                        {uploadResult.errors.map((it: string, idx: number) => (
                            <div key={idx}>{it}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const uploadOdd = () => {
        setUploadResult(undefined);
        setIsLoading(true);
        setProgress("Uploading file...");
        const formData = new FormData();
        formData.append("file", throwErrorOnNullOrUndefined(selectedFile), selectedFile!.name);
        OddIqApi.startUpload(formData)
            .then(({data}) => {
                if (data?.jobId) {
                    setJobId(data.jobId);
                    setPollingStartTime(Date.now());
                    setProgress("Processing started...");
                } else {
                    stopPolling({isSuccess: false, errors: [ERROR_START_UPLOAD_FAILED]});
                }
            })
            .catch((error) => {
                clearFileInput();
                stopPolling({
                    isSuccess: false,
                    errors: [`Upload failed: response: ${error.errorNumber || "Unknown"} reason: ${error.message || "An unexpected error occurred"}`]
                });
            });
    };

    const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        setSelectedFile(e.target.files[0]);
    };

    const clearFileInput = () => {
        setSelectedFile(undefined);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    return <div className="upload-odd__card">
        <h2 className="upload-odd__card-title">Load ODD Data</h2>
        <p className="upload-odd__card-description">Upload an ODD data file to refresh the ODD IQ Dataset. The file must
            be in the exact required <strong>.xlsx</strong> format.</p>

        <TimerComponent isRunning={isLoading}/>
        {isLoading && (
            <div className="upload-odd__loading-spinner-container">
                <LoadingSpinner/>
                {progress && <div className="upload-odd__progress-text" data-testid="odd__progress">{progress}</div>}
            </div>
        )}

        <div className="upload-odd__file-selection-area" style={{ display: selectedFile || isLoading ? 'none' : 'flex' }}>
            <button
                className="upload-odd__btn upload-odd__btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
            >
                <i className="fa-solid fa-paperclip" /> Choose File
            </button>
            <span className="upload-odd__no-file-text">No file chosen</span>
            <input
                ref={fileInputRef}
                id="file-input"
                onChange={handleFileSelection}
                type="file"
                className="upload-odd__file-input"
                accept=".xlsx"
                disabled={isLoading}
                data-testid="odd-file-input"
            />
        </div>

        <div
            className={`upload-odd__file-selected ${selectedFile && !isLoading ? 'upload-odd__file-selected--active' : ''}`}>
            <i className="fa-solid fa-file-excel upload-odd__file-icon"/>
            <div className="upload-odd__file-details">
                <div className="upload-odd__file-name">{selectedFile?.name}</div>
                <div className="upload-odd__file-size">{selectedFile ? formatBytes(selectedFile.size) : ''}</div>
            </div>
            <button
                className="upload-odd__remove-file"
                aria-label="Remove file"
                onClick={clearFileInput}
                disabled={isLoading}
            >
                <i className="fa-solid fa-xmark" />
            </button>
        </div>

        <div className="upload-odd__button-group">
            <button
                className="upload-odd__btn upload-odd__btn-secondary"
                onClick={clearFileInput}
                disabled={isLoading || !selectedFile}
            >
                Cancel
            </button>
            <button
                className="upload-odd__btn upload-odd__btn-primary"
                onClick={uploadOdd}
                disabled={isLoading || !selectedFile}
            >
                Upload Data
            </button>
        </div>

        {renderResult()}
    </div>;
};
